import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to track page visibility state using the Page Visibility API.
 * Returns whether the page is currently visible and provides a way to
 * subscribe to visibility changes.
 *
 * This is useful for pausing expensive operations (SSE connections,
 * animations, polling) when the tab is hidden to reduce resource consumption.
 */
export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState(() => !document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}

/**
 * Hook for managing SSE connections with visibility-aware behavior.
 * Automatically disconnects when tab is hidden and reconnects when visible.
 *
 * @param createConnection - Function that creates and returns an EventSource
 * @param enabled - Whether the connection should be active (when visible)
 */
export function useVisibilityAwareSSE(
  createConnection: () => EventSource | null,
  enabled: boolean = true
): { isConnected: boolean; reconnect: () => void } {
  const isVisible = usePageVisibility();
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    if (!enabled) return;

    const es = createConnection();
    if (es) {
      setEventSource(es);
      setIsConnected(true);
    }
  }, [createConnection, enabled]);

  const disconnect = useCallback(() => {
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
      setIsConnected(false);
    }
  }, [eventSource]);

  const reconnect = useCallback(() => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  // Handle visibility changes
  useEffect(() => {
    if (isVisible && enabled && !eventSource) {
      connect();
    } else if (!isVisible && eventSource) {
      disconnect();
    }
  }, [isVisible, enabled, eventSource, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  return { isConnected, reconnect };
}
